import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const RoleManagementPanel = ({ onCreateRole, onUpdateRole, onDeleteRole }) => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [isCreatingRole, setIsCreatingRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');

  const roles = [
    {
      id: 1,
      name: 'IT Admin',
      description: 'Full system administration access',
      userCount: 5,
      permissions: {
        'User Management': ['create', 'read', 'update', 'delete'],
        'Asset Management': ['create', 'read', 'update', 'delete'],
        'Audit Management': ['create', 'read', 'update', 'delete'],
        'System Configuration': ['create', 'read', 'update', 'delete'],
        'Reports & Analytics': ['create', 'read', 'update', 'delete']
      }
    },
    {
      id: 2,
      name: 'Asset Manager',
      description: 'Asset lifecycle and inventory management',
      userCount: 12,
      permissions: {
        'User Management': ['read'],
        'Asset Management': ['create', 'read', 'update', 'delete'],
        'Audit Management': ['read', 'update'],
        'System Configuration': ['read'],
        'Reports & Analytics': ['read', 'update']
      }
    },
    {
      id: 3,
      name: 'Auditor',
      description: 'Audit and compliance management',
      userCount: 8,
      permissions: {
        'User Management': ['read'],
        'Asset Management': ['read', 'update'],
        'Audit Management': ['create', 'read', 'update'],
        'System Configuration': ['read'],
        'Reports & Analytics': ['read', 'update']
      }
    },
    {
      id: 4,
      name: 'Standard User',
      description: 'Basic asset viewing and reporting',
      userCount: 45,
      permissions: {
        'User Management': [],
        'Asset Management': ['read'],
        'Audit Management': ['read'],
        'System Configuration': [],
        'Reports & Analytics': ['read']
      }
    }
  ];

  const permissionModules = [
    'User Management',
    'Asset Management',
    'Audit Management',
    'System Configuration',
    'Reports & Analytics'
  ];

  const permissionTypes = ['create', 'read', 'update', 'delete'];

  const handleCreateRole = () => {
    if (newRoleName?.trim()) {
      const newRole = {
        id: Date.now(),
        name: newRoleName,
        description: newRoleDescription,
        userCount: 0,
        permissions: permissionModules?.reduce((acc, module) => {
          acc[module] = [];
          return acc;
        }, {})
      };
      onCreateRole(newRole);
      setNewRoleName('');
      setNewRoleDescription('');
      setIsCreatingRole(false);
    }
  };

  const handlePermissionChange = (roleId, module, permission, checked) => {
    const role = roles?.find(r => r?.id === roleId);
    if (role) {
      const updatedPermissions = { ...role?.permissions };
      if (checked) {
        updatedPermissions[module] = [...updatedPermissions?.[module], permission];
      } else {
        updatedPermissions[module] = updatedPermissions?.[module]?.filter(p => p !== permission);
      }
      onUpdateRole(roleId, { permissions: updatedPermissions });
    }
  };

  const getPermissionIcon = (permission) => {
    switch (permission) {
      case 'create':
        return 'Plus';
      case 'read':
        return 'Eye';
      case 'update':
        return 'Edit';
      case 'delete':
        return 'Trash2';
      default:
        return 'Circle';
    }
  };

  const getPermissionColor = (permission) => {
    switch (permission) {
      case 'create':
        return 'text-success';
      case 'read':
        return 'text-accent';
      case 'update':
        return 'text-warning';
      case 'delete':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Role Management</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Configure roles and permissions for system access control
            </p>
          </div>
          <Button
            variant="default"
            onClick={() => setIsCreatingRole(true)}
            iconName="Plus"
            iconPosition="left"
          >
            Create Role
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-border">
        {/* Roles List */}
        <div className="p-6">
          <h4 className="font-medium text-card-foreground mb-4">System Roles</h4>
          <div className="space-y-3">
            {roles?.map((role) => (
              <div
                key={role?.id}
                className={`p-3 rounded-lg border cursor-pointer transition-smooth ${
                  selectedRole?.id === role?.id
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
                onClick={() => setSelectedRole(role)}
              >
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-card-foreground">{role?.name}</h5>
                  <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded">
                    {role?.userCount} users
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{role?.description}</p>
              </div>
            ))}

            {/* Create New Role Form */}
            {isCreatingRole && (
              <div className="p-3 border border-primary rounded-lg bg-primary/5">
                <Input
                  label="Role Name"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e?.target?.value)}
                  placeholder="Enter role name"
                  className="mb-3"
                />
                <Input
                  label="Description"
                  value={newRoleDescription}
                  onChange={(e) => setNewRoleDescription(e?.target?.value)}
                  placeholder="Enter role description"
                  className="mb-3"
                />
                <div className="flex items-center space-x-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleCreateRole}
                    disabled={!newRoleName?.trim()}
                  >
                    Create
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsCreatingRole(false);
                      setNewRoleName('');
                      setNewRoleDescription('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Permission Matrix */}
        <div className="lg:col-span-2 p-6">
          {selectedRole ? (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h4 className="font-medium text-card-foreground">{selectedRole?.name} Permissions</h4>
                  <p className="text-sm text-muted-foreground">{selectedRole?.description}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Copy"
                    iconPosition="left"
                  >
                    Clone Role
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => onDeleteRole(selectedRole?.id)}
                    iconName="Trash2"
                    iconPosition="left"
                    disabled={selectedRole?.userCount > 0}
                  >
                    Delete
                  </Button>
                </div>
              </div>

              <div className="space-y-6">
                {permissionModules?.map((module) => (
                  <div key={module} className="border border-border rounded-lg p-4">
                    <h5 className="font-medium text-card-foreground mb-3">{module}</h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {permissionTypes?.map((permission) => (
                        <div key={permission} className="flex items-center space-x-2">
                          <Checkbox
                            checked={selectedRole?.permissions?.[module]?.includes(permission) || false}
                            onChange={(e) => handlePermissionChange(
                              selectedRole?.id,
                              module,
                              permission,
                              e?.target?.checked
                            )}
                          />
                          <div className="flex items-center space-x-1">
                            <Icon
                              name={getPermissionIcon(permission)}
                              size={14}
                              className={getPermissionColor(permission)}
                            />
                            <span className="text-sm text-card-foreground capitalize">
                              {permission}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Info" size={16} />
                  <span>
                    Changes to role permissions will take effect immediately for all users assigned to this role.
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Icon name="Shield" size={48} className="text-muted-foreground mb-4" />
              <h4 className="text-lg font-medium text-card-foreground mb-2">Select a Role</h4>
              <p className="text-muted-foreground">
                Choose a role from the list to view and edit its permissions.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleManagementPanel;